import { useSuspenseQuery } from '@apollo/client'
import {
  FetchEltModelsDocument,
  type FetchEltModelsQuery,
  FetchSheetCustomModelsDocument,
  type SheetCustomModelFieldsFragment,
} from '@salescore/client-api'
import type { ViewConfig } from '@salescore/core'
import { BUILTIN_USER_ROLES } from '@salescore/features'
import { useMemo } from 'react'

import { type CanSalescoreObjectAction, createCanSalescoreObjectAction } from '../misc/createCanSalescoreObjectAction'
import { type Me, useMe } from './useMe'

/**
 * シートに表示するカスタムオブジェクトについて、個別権限で許可された操作を返す
 * - 戻り値はシートの描画中に何度も参照されるため、権限・シート設定が変わらない限り同じ参照を返す
 */
export const useCanForSalescoreObject = ({
  id,
  config,
}: {
  id: string
  config: ViewConfig
}): CanSalescoreObjectAction => {
  const me = useMe()
  // cacheがあるとはいえ転送量が大きいため本当はmodelを全て引きたくない。BE(eltModels)で絞り込みできるようにするべきか
  const {
    data: { eltModels },
  } = useSuspenseQuery(FetchEltModelsDocument, {
    variables: {
      organizationId: me.organization.id,
    },
    fetchPolicy: 'cache-first',
  })
  const {
    data: { sheetCustomModels },
  } = useSuspenseQuery(FetchSheetCustomModelsDocument, {
    variables: {
      organizationId: me.organization.id,
      viewId: id,
    },
  })
  const {
    salescoreObjectPermissionUsers,
    salescoreObjectPermissionUserGroups,
    myUser: { role },
  } = me

  return useMemo(
    () =>
      buildCanSalescoreObjectAction({
        config,
        role,
        eltModels,
        sheetCustomModels,
        me: { salescoreObjectPermissionUsers, salescoreObjectPermissionUserGroups },
      }),
    [eltModels, sheetCustomModels, role, salescoreObjectPermissionUsers, salescoreObjectPermissionUserGroups, config],
  )
}

/**
 * シートに表示するカスタムオブジェクトについて、個別権限で許可された操作を求める
 * - シート以外のビューと、ミニマルユーザーにはどの操作も許可しない
 */
const buildCanSalescoreObjectAction = ({
  config,
  role,
  eltModels,
  sheetCustomModels,
  me,
}: {
  config: ViewConfig
  role: string
  eltModels: FetchEltModelsQuery['eltModels']
  sheetCustomModels: SheetCustomModelFieldsFragment[]
  me: Pick<Me, 'salescoreObjectPermissionUsers' | 'salescoreObjectPermissionUserGroups'>
}): CanSalescoreObjectAction => {
  if (config.type !== 'sheet' || role === BUILTIN_USER_ROLES.minimal) {
    return {
      canSaveSalescoreObject: false,
      canEditSalescoreObjectRecord: false,
      canDeleteSalescoreObjectRecord: false,
      canCreateSalescoreObjectRecord: false,
      editableModelNames: [],
    }
  }

  return createCanSalescoreObjectAction({ eltModels, sheetCustomModels, config, me })
}
